import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Search, Download, X, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

const ExcelViewer = ({ data, selectedSheet, onSheetChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const tableRef = useRef(null);
  const headerRef = useRef(null);

  // Get current sheet data
  const currentSheetData = data[selectedSheet]?.data || [];
  
  // Extract headers (first row)
  const headers = currentSheetData[0] || [];
  const dataRows = currentSheetData.slice(1);

  // Calculate column widths based on content
  useEffect(() => {
    if (currentSheetData.length > 0) {
      const widths = {};
      headers.forEach((header, index) => {
        // Get max width for this column
        let maxWidth = String(header || '').length * 8;
        dataRows.forEach(row => {
          const cellWidth = String(row[index] || '').length * 8;
          maxWidth = Math.max(maxWidth, cellWidth);
        });
        widths[index] = Math.min(Math.max(maxWidth, 80), 400); // Min 80px, max 400px
      });
      setColumnWidths(widths);
    }
  }, [selectedSheet, currentSheetData]);

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = dataRows;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(row =>
        row.some(cell =>
          String(cell || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply sorting
    if (sortColumn !== null) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortColumn] || '';
        const bVal = b[sortColumn] || '';
        
        // Try to parse as numbers first
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // Fall back to string comparison
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [dataRows, searchTerm, sortColumn, sortDirection]);

  // Handle column sorting
  const handleSort = (columnIndex) => {
    if (sortColumn === columnIndex) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnIndex);
      setSortDirection('asc');
    }
  };

  // Handle column resize
  const handleMouseDown = (e, columnIndex) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnIndex);
    
    const startX = e.pageX;
    const startWidth = columnWidths[columnIndex] || 100;
    
    const handleMouseMove = (e) => {
      const diff = e.pageX - startX;
      const newWidth = Math.max(50, startWidth + diff);
      setColumnWidths(prev => ({
        ...prev,
        [columnIndex]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Format cell value for display
  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    
    // Check if it's a date (Excel stores dates as numbers)
    if (typeof value === 'number' && value > 25569 && value < 50000) {
      // Likely an Excel date
      const date = new Date((value - 25569) * 86400 * 1000);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    
    // Format numbers with commas if they're large
    if (typeof value === 'number') {
      if (Number.isInteger(value) && Math.abs(value) > 999) {
        return value.toLocaleString();
      }
      if (!Number.isInteger(value)) {
        return value.toFixed(2);
      }
    }
    
    return String(value);
  };

  // Export filtered data
  const handleExport = () => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...processedData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, data[selectedSheet].name);
    XLSX.writeFile(wb, `${data[selectedSheet].name}_filtered.xlsx`);
  };

  // Calculate stats for numeric columns
  const columnStats = useMemo(() => {
    const stats = {};
    headers.forEach((header, index) => {
      const columnData = processedData.map(row => row[index]).filter(val => val !== null && val !== undefined);
      const numericData = columnData.map(val => parseFloat(val)).filter(val => !isNaN(val));
      
      if (numericData.length > 0) {
        stats[index] = {
          sum: numericData.reduce((a, b) => a + b, 0),
          avg: numericData.reduce((a, b) => a + b, 0) / numericData.length,
          min: Math.min(...numericData),
          max: Math.max(...numericData),
          count: numericData.length
        };
      }
    });
    return stats;
  }, [headers, processedData]);

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 p-3">
        <div className="flex items-center justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search in spreadsheet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Stats */}
          <div className="text-sm text-gray-600">
            {processedData.length} rows
            {searchTerm && ` (filtered from ${dataRows.length})`}
          </div>
          
          {/* Export */}
          <button
            onClick={handleExport}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Filtered
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-grow relative overflow-hidden">
        <div className="absolute inset-0 overflow-auto overscroll-contain" ref={tableRef} style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="w-full border-collapse">
            {/* Sticky Header */}
            <thead className="sticky top-0 z-10 bg-gray-100" ref={headerRef}>
              <tr>
                <th className="sticky left-0 z-20 bg-gray-200 border border-gray-300 px-2 py-2 text-xs font-semibold text-gray-700 text-center w-12">
                  #
                </th>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="relative border border-gray-300 bg-gray-100 text-left group"
                    style={{ width: columnWidths[index] || 'auto', minWidth: columnWidths[index] || 'auto' }}
                  >
                    <div
                      className="px-3 py-2 text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 flex items-center justify-between"
                      onClick={() => handleSort(index)}
                    >
                      <span className="truncate mr-2">{header || `Column ${index + 1}`}</span>
                      <div className="flex items-center gap-1">
                        {sortColumn === index && (
                          <span className="text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                        {columnStats[index] && (
                          <Filter className="h-3 w-3 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {/* Resize handle */}
                    <div
                      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 bg-gray-300"
                      onMouseDown={(e) => handleMouseDown(e, index)}
                      style={{ 
                        backgroundColor: resizingColumn === index ? '#3B82F6' : undefined 
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            
            {/* Table Body */}
            <tbody>
              {processedData.length > 0 ? (
                processedData.map((row, rowIndex) => (
                  <tr 
                    key={rowIndex}
                    className="hover:bg-gray-50 group"
                  >
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 border border-gray-300 px-2 py-1 text-xs text-gray-500 text-center font-medium">
                      {rowIndex + 2}
                    </td>
                    {headers.map((_, cellIndex) => {
                      const value = row[cellIndex];
                      const isNumeric = !isNaN(parseFloat(value));
                      
                      return (
                        <td
                          key={cellIndex}
                          className={`border border-gray-300 px-3 py-1 text-sm ${
                            isNumeric ? 'text-right' : 'text-left'
                          }`}
                          style={{ 
                            maxWidth: columnWidths[cellIndex] || 'auto',
                            width: columnWidths[cellIndex] || 'auto'
                          }}
                        >
                          <div className="truncate" title={formatCellValue(value)}>
                            {formatCellValue(value)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td 
                    colSpan={headers.length + 1} 
                    className="text-center py-8 text-gray-500"
                  >
                    {searchTerm ? 'No results found' : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
            
            {/* Summary Footer for numeric columns */}
            {Object.keys(columnStats).length > 0 && (
              <tfoot className="sticky bottom-0 z-10 bg-gray-100 border-t-2 border-gray-400">
                <tr>
                  <td className="sticky left-0 z-20 bg-gray-200 border border-gray-300 px-2 py-2 text-xs font-semibold text-gray-700">
                    Σ
                  </td>
                  {headers.map((_, index) => (
                    <td
                      key={index}
                      className="border border-gray-300 px-3 py-2 text-xs text-gray-700 text-right bg-gray-100"
                    >
                      {columnStats[index] ? (
                        <div className="space-y-1">
                          <div title="Sum">Σ: {columnStats[index].sum.toLocaleString()}</div>
                          <div title="Average">μ: {columnStats[index].avg.toFixed(2)}</div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExcelViewer;